import React, { useState, useEffect } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import DownloadIcon from '@mui/icons-material/Download'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import apiConfig from '../../config/api'

const API_BASE = apiConfig.baseUrl

/**
 * PDF Viewer Modal Component
 * Fetches PDF with auth headers and displays in an iframe
 *
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Close handler
 * @param {string} filename - The PDF filename to display
 * @param {string} title - Optional title for the dialog
 * @param {string} type - 'datasheet' or 'image' (default: 'datasheet')
 */
const PDFViewer = ({
  open,
  onClose,
  filename,
  title = 'Document Viewer',
  type = 'datasheet'
}) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [blobUrl, setBlobUrl] = useState(null)

  useEffect(() => {
    if (open && filename) {
      fetchPDF()
    }

    return () => {
      // Clean up blob URL when component unmounts or dialog closes
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl)
        setBlobUrl(null)
      }
    }
  }, [open, filename])

  const fetchPDF = async () => {
    setLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('jwtToken') || sessionStorage.getItem('jwtToken')

      if (!token) {
        throw new Error('Not authenticated')
      }

      const endpoint = type === 'image'
        ? `${API_BASE}/files/image/${filename}`
        : `${API_BASE}/files/datasheet/${filename}`

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication required. Please log in again.')
        } else if (response.status === 404) {
          throw new Error('File not found')
        }
        throw new Error(`Failed to load file: ${response.statusText}`)
      }

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      setBlobUrl(url)
    } catch (err) {
      console.error('Error fetching PDF:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = () => {
    if (blobUrl) {
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const handleOpenNewTab = () => {
    if (blobUrl) {
      window.open(blobUrl, '_blank')
    }
  }

  const handleClose = () => {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl)
      setBlobUrl(null)
    }
    setError(null)
    setLoading(true)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="span" sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {blobUrl && (
            <>
              <IconButton onClick={handleDownload} title="Download">
                <DownloadIcon />
              </IconButton>
              <IconButton onClick={handleOpenNewTab} title="Open in new tab">
                <OpenInNewIcon />
              </IconButton>
            </>
          )}
          <IconButton onClick={handleClose} title="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, p: 3 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {blobUrl && !loading && !error && (
          <iframe
            src={blobUrl}
            title={title}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              flex: 1
            }}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default PDFViewer
